import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Shield, Mail, Clock, Users, FileText, Trash2, Edit, Eye, MessageSquare, MapPin
} from "lucide-react";

const ELIDO_LOGO = "https://customer-assets.emergentagent.com/job_custom-demo-gen/artifacts/x2wfas84_Elido_logotype_blue.png";

export default function IntegritetspolicyPage() {
  return (
    <div className="min-h-screen bg-white font-['Inter']" data-testid="integritetspolicy-page">
      <Helmet>
        <title>Integritetspolicy | Elido AB - Så hanterar vi dina uppgifter</title>
        <meta name="description" content="Så samlar Elido AB in, använder, lagrar och skyddar dina personuppgifter enligt GDPR — inklusive vad vår AI-chattagent loggar. Kontakt: hej@elido.nu." />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0F2A30] py-4">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/">
            <img src={ELIDO_LOGO} alt="Elido" className="h-12 md:h-14" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0F2A30] py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#255E69] rounded-2xl mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Integritetspolicy
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Så hanterar vi dina personuppgifter
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Senast uppdaterad: september 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-6">

          {/* Introduction */}
          <section className="mb-12">
            <p className="text-gray-600 text-lg leading-relaxed">
              Elido AB (org.nr 559553-0873) är personuppgiftsansvarig för behandlingar där Elido
              bestämmer ändamål och medel, till exempel när du kontaktar oss, beställer en demo
              eller använder elido.nu. När vår AI-agent används på ett kundföretags webbplats kan
              kundföretaget vara personuppgiftsansvarigt och Elido behandla personuppgifter som
              personuppgiftsbiträde enligt kundens instruktioner. Den här policyn förklarar vilka
              uppgifter som kan behandlas, varför de behandlas, hur länge de behövs, vilka de kan
              delas med och vilka rättigheter du har.
            </p>
          </section>

          {/* Section 1: What we collect */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">1. Vilka uppgifter vi samlar in</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <p>När du besöker vår webbplats eller använder våra tjänster behandlar vi följande typer av uppgifter:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Kontaktuppgifter:</strong> namn, e-postadress, telefonnummer och företagsnamn
                  som du själv anger när du fyller i formulär, beställer en demo eller chattar med oss.
                </li>
                <li>
                  <strong>Demo- och konfigurationsuppgifter:</strong> bransch, stad, paketval och liknande
                  information som behövs för att generera din personliga demo eller offert.
                </li>
                <li>
                  <strong>Användnings- och teknisk data:</strong> IP-adress, webbläsare, enhet, språk,
                  besökta sidor och referrer (varifrån du kom).
                </li>
                <li>
                  <strong>Webb- och analysdata:</strong> information om hur webbplatsen används,
                  exempelvis sidvisningar och interaktioner. Statistik- och marknadsföringsteknik
                  styrs av de val du gör i våra cookieinställningar.
                </li>
              </ul>
            </div>
          </section>

          {/* Section 2: AI chat agent — what we log */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">2. AI-chattagent — vad vi loggar</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <p>
                När du chattar med vår AI-agent (på elido.nu eller hos någon av våra kunder som
                använder vår widget) lagrar vi konversationen för att kunna återkoppla, förbättra
                tjänsten och uppfylla våra avtal med kundföretaget. Det vi loggar i vårt
                administratörsgränssnitt är:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Konversationen:</strong> dina meddelanden och AI-agentens svar i den
                  aktuella sessionen.
                </li>
                <li>
                  <strong>Kontaktuppgifter:</strong> namn, telefon eller e-post som du själv
                  delat i chatten — endast när du faktiskt skrivit in dem.
                </li>
                <li>
                  <strong>Sessions-ID:</strong> en slumpmässig identifierare som kopplar samman
                  meddelanden i samma chatt. Inget personnummer eller inloggning kopplas till
                  sessionen.
                </li>
                <li>
                  <strong>Kundresa:</strong> vilken sida du startade chatten på, eventuell
                  refererande webbplats samt de sidor du besökt under tiden chatten var öppen.
                  Detta hjälper oss/kundföretaget att förstå sammanhanget för din fråga.
                </li>
                <li>
                  <strong>Teknisk data:</strong> IP-adress (kortfattat och i säkerhetssyfte),
                  webbläsare och tidsstämplar.
                </li>
                <li>
                  <strong>Strukturerade uppgifter:</strong> AI-agenten kan strukturera information
                  som framgår av konversationen, till exempel ärendetyp, ort eller andra uppgifter
                  som behövs för att hantera förfrågan.
                </li>
              </ul>
              <div className="bg-[#255E69]/5 border border-[#255E69]/10 p-4 rounded-xl text-sm">
                <strong className="text-gray-900">Rättslig grund:</strong> Den rättsliga grunden
                beror på varför uppgifterna behandlas. När du kontaktar oss för demo, offert,
                återkoppling eller för att diskutera en tjänst kan behandlingen vara nödvändig för
                att vidta åtgärder på din begäran inför ett avtal (art. 6.1 b GDPR) eller bygga på
                vårt berättigade intresse av att hantera förfrågan (art. 6.1 f). Samtycke används
                där vi faktiskt ber om det, exempelvis för icke-nödvändiga cookies och liknande
                teknik. För behandling som Elido utför på ett kundföretags dokumenterade
                instruktion är kundföretaget personuppgiftsansvarigt och Elido personuppgiftsbiträde.
              </div>
            </div>
          </section>

          {/* Section 3: Why */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <Eye className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">3. Varför vi samlar in uppgifter</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Demogenerering & offerter:</strong> skapa personligt anpassat innehåll baserat på dina uppgifter.</li>
                <li><strong>Kommunikation:</strong> svara på frågor och skicka demolänkar, offerter och bekräftelser.</li>
                <li><strong>Tjänsteleverans:</strong> driva och förbättra AI-chattagenten åt oss och våra kunder.</li>
                <li><strong>Säkerhet:</strong> upptäcka spam, prompt-injektion, testdata och missbruk.</li>
                <li><strong>Marknadsföringsspårning på webbplatsen:</strong> används endast enligt dina val för marknadsföringscookies.</li>
              </ul>
              <p className="mt-4">
                <strong>Rättslig grund:</strong> Vilken rättslig grund som används beror på den
                aktuella behandlingen och kan vara avtal eller åtgärder inför avtal (art. 6.1 b),
                berättigat intresse (art. 6.1 f), samtycke (art. 6.1 a) eller rättslig förpliktelse
                (art. 6.1 c).
              </p>
            </div>
          </section>

          {/* Section 4: Storage time */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">4. Hur länge vi lagrar uppgifter</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <p>
                Vi sparar personuppgifter inte längre än vad som behövs för det ändamål de samlades
                in för. Kontakt-, lead- och demouppgifter sparas så länge de behövs för att hantera
                din förfrågan, leverera tjänsten och följa upp kundrelationen. Tekniska uppgifter
                och säkerhetsdata sparas under en begränsad tid utifrån behovet av drift, felsökning
                och säkerhet. Uppgifter kan behöva sparas längre när det krävs enligt lag eller för
                att fastställa, göra gällande eller försvara rättsliga anspråk. Statistik som inte
                längre kan kopplas till en identifierbar person kan sparas längre.
              </p>
            </div>
          </section>

          {/* Section 5: Third parties */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">5. Leverantörer och mottagare</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <p>
                För att leverera och utveckla våra tjänster använder vi leverantörer inom bland
                annat hosting och databas, e-post, AI-tjänster samt analys och annonsering. Resend
                används för transaktionella mejl och leadnotiser. På elido.nu används Google
                Analytics och, beroende på dina cookieval, Google Ads och Meta Pixel för mätning och
                marknadsföring. AI-leverantörer kan behandla innehåll som behöver skickas till en
                språkmodell för att AI-funktionen ska kunna besvara din fråga. Vi begränsar
                uppgifterna till vad som behövs för tjänsten. När en leverantör behandlar
                personuppgifter som vårt personuppgiftsbiträde använder vi personuppgiftsbiträdesavtal
                där GDPR kräver det.
              </p>
              <p className="mt-4 font-medium text-gray-700">
                Vi säljer aldrig dina personuppgifter till tredje part.
              </p>
              <h3 className="text-lg font-bold text-gray-900 mt-6">
                Google Ads API och anslutna Google-konton
              </h3>
              <p>
                När ett Google Ads-konto kopplas till en av Elidos tjänster kan Elido, efter
                godkännande från en behörig användare, få åtkomst till Google Ads-data via Google
                Ads API. Det kan exempelvis omfatta information om annonskonton, kampanjer, annonser,
                budgetar och resultatdata. Uppgifterna används för att tillhandahålla funktioner som
                rapportering, analys och hantering av Google Ads enligt den behörighet och de
                instruktioner som lämnats. Vi använder inte Google-data för andra ändamål än de
                funktioner som tjänsten tillhandahåller. Google-data säljs inte. Uppgifter delas
                endast med leverantörer när det behövs för att tillhandahålla tjänsten eller när vi
                är skyldiga enligt lag. Åtkomst till ett Google-konto kan återkallas genom Googles
                kontoinställningar.
              </p>
            </div>
          </section>

          {/* Section 6: Customer journey */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">6. Överföring utanför EU/EES</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <p>
                Vissa leverantörer kan behandla personuppgifter utanför EU/EES. När en sådan
                överföring sker ska den ha stöd i GDPR:s regler om internationella överföringar,
                exempelvis ett beslut om adekvat skyddsnivå eller EU-kommissionens
                standardavtalsklausuler (SCC), med kompletterande skyddsåtgärder när det krävs.
              </p>
            </div>
          </section>

          {/* Section 7: Your rights */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">7. Dina rättigheter enligt GDPR</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <p>Som registrerad har du följande rättigheter:</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-[#255E69]" />
                    <strong className="text-gray-900">Rätt till tillgång</strong>
                  </div>
                  <p className="text-sm">Begär ett registerutdrag över de uppgifter vi har om dig.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Edit className="w-4 h-4 text-[#255E69]" />
                    <strong className="text-gray-900">Rätt till rättelse</strong>
                  </div>
                  <p className="text-sm">Begär att vi korrigerar felaktiga eller ofullständiga uppgifter.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Trash2 className="w-4 h-4 text-[#255E69]" />
                    <strong className="text-gray-900">Rätt till radering</strong>
                  </div>
                  <p className="text-sm">Begär att vi raderar dina personuppgifter ("rätten att bli glömd").</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-[#255E69]" />
                    <strong className="text-gray-900">Rätt till dataportabilitet</strong>
                  </div>
                  <p className="text-sm">Få ut dina uppgifter i ett maskinläsbart format.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-[#255E69]" />
                    <strong className="text-gray-900">Rätt att invända</strong>
                  </div>
                  <p className="text-sm">Invända mot behandling som sker baserat på berättigat intresse.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-[#255E69]" />
                    <strong className="text-gray-900">Rätt till begränsning</strong>
                  </div>
                  <p className="text-sm">Begära att behandlingen begränsas medan ärendet utreds.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-[#255E69]" />
                    <strong className="text-gray-900">Rätt att återkalla samtycke</strong>
                  </div>
                  <p className="text-sm">Om en behandling bygger på ditt samtycke kan du när som helst återkalla det. Det påverkar inte lagligheten av behandling som redan skett.</p>
                </div>
              </div>
              <p className="mt-4">
                För att utöva dina rättigheter, kontakta oss på{" "}
                <a href="mailto:hej@elido.nu" className="text-[#255E69] hover:underline font-medium">
                  hej@elido.nu
                </a>
                . Vi hanterar din begäran utan onödigt dröjsmål och normalt senast inom en månad. I
                vissa mer komplicerade fall får tidsfristen förlängas enligt GDPR, och då informerar
                vi dig.
              </p>
            </div>
          </section>

          {/* Section 8: Cookies */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">8. Cookies</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <p>
                Vi använder nödvändig lagring för att webbplatsen och efterfrågade funktioner ska
                fungera. Statistik och marknadsföring styrs av dina val i vår cookiebanner. Du kan
                när som helst ändra dina val via ‘Cookieinställningar’ i sidfoten. Läs mer i vår{" "}
                <Link to="/cookies" className="text-[#255E69] hover:underline font-medium">
                  cookiepolicy
                </Link>.
              </p>
            </div>
          </section>

          {/* Section 9: Contact */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#255E69]/10 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#255E69]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">9. Kontakta oss</h2>
            </div>
            <div className="pl-13 space-y-4 text-gray-600">
              <p>
                Har du frågor om hur vi hanterar dina personuppgifter — eller vill du utöva
                någon av dina rättigheter? Kontakta oss:
              </p>
              <div className="bg-[#255E69]/5 border border-[#255E69]/10 p-6 rounded-xl">
                <p className="font-semibold text-gray-900 mb-2">Elido AB</p>
                <p>Org.nr: 559553-0873</p>
                <p>E-post:{" "}
                  <a href="mailto:hej@elido.nu" className="text-[#255E69] hover:underline">
                    hej@elido.nu
                  </a>
                </p>
                <p className="mt-4 text-sm text-gray-500">
                  Du har även rätt att lämna klagomål till{" "}
                  <a
                    href="https://www.imy.se/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#255E69] hover:underline"
                  >
                    Integritetsskyddsmyndigheten (IMY)
                  </a>{" "}
                  om du anser att vi behandlar dina uppgifter felaktigt.
                </p>
              </div>
            </div>
          </section>

          {/* Back link */}
          <div className="border-t border-gray-100 pt-8 mt-12">
            <Link
              to="/"
              className="text-[#255E69] hover:underline font-medium inline-flex items-center gap-2"
              data-testid="back-to-home-link"
            >
              ← Tillbaka till startsidan
            </Link>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0F2A30] py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Elido AB. Alla rättigheter förbehållna.
          </p>
          <p className="text-gray-600 text-xs mt-1">
            Egenutvecklad plattform optimerad för att generera fler kunder
          </p>
        </div>
      </footer>
    </div>
  );
}
